import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import { cssTransform } from 'utils/css-utils';
import { createConfigLayout, getElementAlign } from 'utils/project-definition/config-layout';
import { getConfigNotes } from 'utils/project-definition/note-utils';
import DragManager from 'utils/project-definition/drag-manager';

import DragLayer from './drag-layer';
import BoxLine from './box-line';
import ConfigComponent from './config-component';

import NoteLayer from './note-layer';

const START_GRID_WIDTH = 1000;
const START_GRID_HEIGHT = 800;
const ZOOM_STEP = 0.1;

// Grid align settings (0 = left or top, 0.5 = middle, 1 = right or bottom)
const GRID_ALIGN_X = 0.5;
const GRID_ALIGN_Y = 1;

const MIN_ZOOM = 0.1;
const MAX_ZOOM = 2;

const DEFAULT_ZOOM = 'contain';
const DEFAULT_VIEW_ALIGN_X = 0.5;
const DEFAULT_VIEW_ALIGN_Y = 1;

export default class ConfigView extends React.Component {
  constructor(props) {
    super(props);

    // Bind methods

    this.getTranslation = this.getTranslation.bind(this);
    this.getContainerRefHandler = this.getContainerRefHandler.bind(this);
    this.resizeWindowHandler = this.resizeWindowHandler.bind(this);
    this.workspaceRefHandler = this.workspaceRefHandler.bind(this);

    this.noteLayerRefHandler = this.noteLayerRefHandler.bind(this);
    this.updateNoteContentHandler = this.updateNoteContentHandler.bind(this);
    this.pressNoteHandler = this.pressNoteHandler.bind(this);

    this.resetZoom = this.resetZoom.bind(this);
    this.zoomToFit = this.zoomToFit.bind(this);
    this.zoomIn = this.zoomIn.bind(this);
    this.zoomOut = this.zoomOut.bind(this);
    this.panGridHandler = this.panGridHandler.bind(this);

    this.clickComponentHandler = this.clickComponentHandler.bind(this);
    this.clickBackgroundHandler = this.clickBackgroundHandler.bind(this);

    this.componentImageLoadedHandler = this.componentImageLoadedHandler.bind(this);

    // Init state

    this.clickDragManager = new DragManager();
    this.clickDragBackgroundManager = new DragManager();

    let minZoom = DEFAULT_ZOOM;
    if (typeof (minZoom) !== 'string') {
      minZoom = 'cover';
    }

    let startGridX = START_GRID_WIDTH * DEFAULT_VIEW_ALIGN_X;
    let startGridY = START_GRID_HEIGHT * DEFAULT_VIEW_ALIGN_Y;
    let startZoom = DEFAULT_ZOOM;

    if (props) {
      if (typeof (props.viewZoom) === 'number') {
        startZoom = props.viewZoom;
      }
      if (typeof (props.viewX) === 'number') {
        startGridX = props.viewX;
      }
      if (typeof (props.viewY) === 'number') {
        startGridY = props.viewY;
      }
    }

    if (typeof (startZoom) === 'string') {
      minZoom = startZoom;
    }
    this.state = {
      gridWidth: START_GRID_WIDTH,
      gridHeight: START_GRID_HEIGHT,
      gridTop: 0,
      gridLeft: 0,
      viewport: {
        minZoom,
        zoom: startZoom, // 'fit' or 'contain', 'fill' or 'cover', or any numeric value between MIN_ZOOM and MAX_ZOOM
        x: startGridX,
        y: startGridY,
      },
    };
  }

  /* Lifecycle */

  componentWillReceiveProps(newProps) {
    this.tryUpdateLayout(newProps);
  }

  componentWillMount() {
    window.addEventListener('resize', this.resizeWindowHandler);
    this.tryUpdateLayout(this.props);
  }

  componentDidMount() {
    // Ok, this is an anti-pattern but I'm losing my patience now
    this.mounted = true;
  }

  componentWillUnmount() {
    this.mounted = false;
    window.removeEventListener('resize', this.resizeWindowHandler);
  }

  /* Event handlers */

  componentImageLoadedHandler(img, comp) {
    const { loadingURLs } = this;
    const src = comp.getComponentImageURL();
    if (!loadingURLs) {
      this.notifyLoaded();
      return;
    }

    const urlIndex = loadingURLs.indexOf(src);
    if (urlIndex >= 0) {
      loadingURLs.splice(urlIndex, 1);
    }
    if (!loadingURLs.length) {
      this.notifyLoaded();
    }
  }

  noteLayerRefHandler(ref) {
    this.noteLayerRef = ref;
  }

  pressNoteHandler(note, noteIndex, layerComponent, noteComponent, evt) {
    const { onPressNote } = this.props;

    if (onPressNote) {
      onPressNote(note, noteIndex, this, noteComponent, evt);
    }
  }

  getContainerRefHandler(container) {
    this.container = container;
    this.updateContainerSize();
    this.updatePan();
  }

  resizeWindowHandler() {
    this.updateContainerSize();
    this.updatePan();
  }

  clickComponentHandler(object, comp) {
    if (this.props.onClickComponent) {
      this.props.onClickComponent(object, comp);
    }
  }

  clickBackgroundHandler(evt, pressEvent) {
    this.closeOpenedNote();
    const { onClickBackground } = this.props;
    if (onClickBackground) {
      onClickBackground(evt, pressEvent, this);
    }
  }

  workspaceRefHandler(ref) {
    if (this.props.onWorkspaceRef) {
      this.props.onWorkspaceRef(ref);
    }
  }

  panGridHandler(e) {
    if (!this.isPanningAllowed()) {
      return;
    }
    const viewportX = this.getViewportX();
    const viewportY = this.getViewportY();
    const zoom = this.getCalcZoom();
    const movementFactor = 1 / zoom;

    DragLayer.calculateDelta(e.info, e.previousInfo, e.info);
    const { deltaX, deltaY } = e.info;

    const left = viewportX - (deltaX * movementFactor);
    const top = viewportY - (deltaY * movementFactor);

    this.setPan(left, top);
  }

  updateNoteContentHandler(note) {
    const { onUpdateNoteContent } = this.props;
    if (onUpdateNoteContent) {
      onUpdateNoteContent(note, this);
    }
  }

  /* Methods */

  layoutFixesAfterLoad() {
    const { currentLayout } = this;

    if (!currentLayout) {
      return;
    }
    const { elementsByName } = currentLayout;

    const { kettle: kettleData, exhaust: chimneyData } = elementsByName;

    // Fix position for chimney & kettle
    if (chimneyData && kettleData) {
      const { imageInfo } = kettleData;
      if (imageInfo) {
        const { imageBounds } = imageInfo;
        const alignmentKettle = getElementAlign(kettleData) || { x: 0.5, y: 0.5 };
        const alignmentChimney = getElementAlign(chimneyData) || { x: 0.5, y: 0.5 };
        const imageInfoChimney = chimneyData.imageInfo;
        const imageBoundsChimney = imageInfoChimney ? imageInfoChimney.imageBounds : null;
        const chimneyHeight = imageBoundsChimney ? imageBoundsChimney.height : 0;

        chimneyData.y = kettleData.y + (imageBounds.height * alignmentKettle.y) - (chimneyHeight * (1 - alignmentChimney.y));

        if (this.mounted && !this.rendering) {
          this.forceUpdate();
        }
      }
    }
  }

  notifyLoaded() {
    this.layoutFixesAfterLoad();

    const { onLoaded } = this.props;
    if (onLoaded) {
      onLoaded();
    }
  }

  hasLoadedCallback() {
    return !!this.props.onLoaded;
  }

  getTranslation(key, fallback = null) {
    const fb = (fallback === null) ? null : `${fallback}*`; // Add asterisk to indicate a fallback message
    if (!key) {
      return fb;
    }
    const { intl } = this.props;
    const messages = intl ? intl.messages : null;
    if (!messages) {
      return fb;
    }
    const res = messages[key];
    if (res === null || typeof (res) === 'undefined') {
      return fb;
    }
    return res;
  }

  notifyUpdateConfig() {
    const { onUpdateConfig } = this.props;
    if (!onUpdateConfig) {
      return;
    }
    onUpdateConfig(this.props.config, this.props.configVersion);
  }

  notifyViewport() {
    if (!this.props.onChangedViewPort) {
      return;
    }
    const absX = this.absoluteViewportX;
    const absY = this.absoluteViewportY;

    const x = this.getViewportX();
    const y = this.getViewportY();
    const zoom = this.getViewportZoom();

    this.props.onChangedViewPort(x, y, zoom, absX, absY, this.getCalcZoom());
  }

  getContainerElement() {
    return this.container;
  }

  updateContainerSize() {
    const container = this.container;

    this.containerWidth = container ? container.offsetWidth : 0;
    this.containerHeight = container ? container.offsetHeight : 0;
  }

  calcZoom(zoom) {
    const zType = typeof (zoom);
    if (zType === 'number') {
      return zoom;
    }
    let res = 1;

    const containerWidth = this.containerWidth || 100;
    const containerHeight = this.containerHeight || 100;

    let boundsWidth = this.getGridWidth();
    let boundsHeight = this.getGridHeight();

    const currentBounds = this.getContentBounds();

    if (currentBounds) {
      const { minx, miny, maxx, maxy } = currentBounds;
      boundsWidth = maxx - minx;
      boundsHeight = maxy - miny;
    }

    const zoomX = containerWidth / boundsWidth;
    const zoomY = containerHeight / boundsHeight;

    if (zoom === 'fit' || zoom === 'contain') {
      res = zoomX < zoomY ? zoomX : zoomY;
    } else if (zoom === 'fill' || zoom === 'cover') {
      res = zoomX > zoomY ? zoomX : zoomY;
    }
    const maxZoom = MAX_ZOOM;
    res = res > maxZoom ? maxZoom : res;

    return res;
  }

  getGridWidth() {
    const { state } = this;
    const width = state ? state.gridWidth : START_GRID_WIDTH;
    if (typeof (width) === 'number') {
      return width;
    }
    return START_GRID_WIDTH;
  }

  getGridHeight() {
    const state = this.state;
    const height = state ? state.gridHeight : START_GRID_HEIGHT;
    if (typeof (height) === 'number') {
      return height;
    }
    return START_GRID_HEIGHT;
  }

  getDefaultViewportX() {
    return this.getGridWidth() * DEFAULT_VIEW_ALIGN_X;
  }

  getDefaultViewportY() {
    return this.getGridHeight() * DEFAULT_VIEW_ALIGN_Y;
  }

  getViewport() {
    return this.state ? this.state.viewport : null;
  }

  getViewportX() {
    const viewport = this.getViewport();
    if (!viewport) {
      return 0;
    }
    const x = viewport.x;
    if (typeof (x) === 'number') {
      return x;
    }
    return this.getDefaultViewportX();
  }

  getViewportY() {
    const viewport = this.getViewport();
    if (!viewport) {
      return 0;
    }
    const y = viewport.y;
    if (typeof (y) === 'number') {
      return y;
    }
    return this.getDefaultViewportY();
  }

  getViewportZoom() {
    const viewport = this.getViewport();
    if (!viewport) {
      return DEFAULT_ZOOM;
    }
    return viewport.zoom || DEFAULT_ZOOM;
  }

  setPanningAllowed(b) {
    this.panningAllowed = b;
  }

  isPanningAllowed() {
    return this.panningAllowed !== false;
  }

  updatePan() {
    const state = this.state;
    const viewport = state.viewport;
    this.setPan(viewport.x, viewport.y);
  }

  updateViewportValues(setStateDirectly = false) {
    const { viewport } = this.state;
    const { x, y, zoom } = viewport;

    const updatedViewport = this.setViewportValues(x, y, zoom, viewport);
    if (setStateDirectly) {
      this.state.viewport = updatedViewport;
      this.notifyViewport();
    } else {
      this.setState({ viewport: updatedViewport }, this.notifyViewport);
    }
  }

  // Sets the values for a viewport state
  // Returns a changed viewport state object
  setViewportValues(xValue, yValue, zoomValue, viewport) {
    const viewp = viewport;

    let x = xValue;
    let y = yValue;

    const zoom = this.calcZoom(zoomValue);
    // const newZoom = this.limitZoom(zoom);
    const newZoom = typeof (zoomValue) === 'string' ? zoomValue : this.limitZoom(zoom);

    const vpWidth = this.containerWidth / zoom;
    const vpHeight = this.containerHeight / zoom;
    const vpHalfWidth = vpWidth * 0.5;
    const vpHalfHeight = vpHeight * 0.5;

    const gridWidth = this.getGridWidth();
    const gridHeight = this.getGridHeight();

    const marginX = -1 / zoom;
    const marginY = -1 / zoom;


    const { limitPan } = this.props;

    if (limitPan) {
      const minX = vpHalfWidth - marginX;
      const minY = vpHalfHeight - marginY;
      const maxX = (gridWidth - vpHalfWidth) + marginX;
      const maxY = (gridHeight - vpHalfHeight) + marginY;

      x = x > maxX ? maxX : x;
      x = x < minX ? minX : x;
      y = y > maxY ? maxY : y;
      y = y < minY ? minY : y;

      const alignX = GRID_ALIGN_X;
      const alignY = GRID_ALIGN_Y;
      if (vpWidth > gridWidth) {
        x = (gridWidth * alignX) + (vpHalfWidth - (vpWidth * alignX));
      }
      if (vpHeight > gridHeight) {
        y = (gridHeight * alignY) + (vpHalfHeight - (vpHeight * alignY));
      }
    }


    if (viewp.x === x && viewp.y === y && viewp.zoom === newZoom) {
      return viewp;
    }
    viewp.x = x;
    viewp.y = y;

    const resZoom = newZoom !== zoom ? newZoom : zoomValue;
    viewp.zoom = resZoom;

    return viewp;
  }

  setPan(left, top) {
    const viewport = this.setViewportValues(left, top, this.getViewportZoom(), this.getViewport());
    this.setState({
      viewport,
    }, this.notifyViewport);
  }

  getZoom() {
    return this.state.viewport.zoom;
  }

  getCalcZoom() {
    return this.calcZoom(this.getZoom());
  }

  zoomIn() {
    this.zoomGrid(ZOOM_STEP);
  }

  zoomOut() {
    this.zoomGrid(-ZOOM_STEP);
  }

  resetZoom() {
    this.setZoom(DEFAULT_ZOOM);
  }

  /**
   * @method getContentBounds
   * @description Returns a rectangle around the contents of the current system elements
   * @param {object} result - Optional preallocated object where the bounding box values will be stored
   * @return {object} - Object containing { minx, miny, maxx, maxy } (same object as result param if not null)
   * */
  getContentBounds(result = null) {
    const { currentLayout } = this;
    const { showFloor } = this.props;
    // Use the computed boundingbox from the current system layout
    const bounds = currentLayout ? currentLayout.bounds : null;
    if (!bounds) {
      if (result) {
        const res = result;
        res.minx = 0;
        res.miny = 0;
        res.maxx = 0;
        res.maxy = 0;
        res.width = 0;
        res.height = 0;
        return res;
      }
      return null;
    }
    const res = result || {};
    const { minx, maxx, miny, maxy } = bounds;

    res.minx = minx;
    res.miny = miny;
    res.maxx = maxx;
    res.maxy = maxy;

    if (showFloor) {
      // Expand the boundingbox to show a little part of the floor
      const includeFloorMinY = -50;
      res.miny = miny > includeFloorMinY ? includeFloorMinY : miny;
    }

    res.width = maxx - minx;
    res.height = maxy - miny;

    return res;
  }

  getZoomToFitViewport() {
    const floorY = this.getFloorY();

    /*
    let includeComments = true;
    if (options) {
      includeComments = options.includeComments !== false;
    }
    */

    let viewportX = 0;
    let viewportY = floorY;
    let viewportScale = DEFAULT_ZOOM;

    const { containerWidth } = this;
    const { containerHeight } = this;

    let boundsWidth = this.getGridWidth();
    let boundsHeight = this.getGridHeight();

    const bounds = this.getContentBounds();
    if (bounds) {
      const { minx, maxx, miny, maxy } = bounds;

      // TODO
      /*
      if (includeComments) {
        const { config } = this.props;
        const notes = getConfigNotes(config);
        const numNotes = notes ? notes.length : 0;
        for (let i = 0; i < numNotes; i += 1) {
          const note = notes[i];
          if (note) {
            const { x } = note;
            let { y } = note;
            y = -(y - floorY);
            minx = (minx < x) ? minx : x;
            maxx = (maxx > x) ? maxx : x;
            miny = (miny < y) ? miny : y;
            maxy = (maxy > y) ? maxy : y;
          }
        }
      }
      */

      boundsWidth = maxx - minx;
      boundsHeight = maxy - miny;

      viewportX = boundsWidth * 0.5;
      viewportY = (floorY - maxy) + (boundsHeight * 0.5);
    }

    if (typeof (DEFAULT_ZOOM) !== 'string' && boundsWidth > 0 && boundsHeight > 0) {
      const viewportScaleX = containerWidth / boundsWidth;
      const viewportScaleY = containerHeight / boundsHeight;

      viewportScale = viewportScaleX < viewportScaleY ? viewportScaleX : viewportScaleY;
    }

    let { viewport } = this.state;
    viewport = this.setViewportValues(viewportX, viewportY, viewportScale, viewport);

    return viewport;
  }

  zoomToFit(options) {
    const viewport = this.getZoomToFitViewport(options);

    this.setState({
      viewport,
    }, this.notifyViewport);
  }

  limitZoom(zoom) {
    let newZoom = zoom;

    let minZoomType = this.state.viewport.minZoom;
    if (minZoomType && minZoomType.toLowerCase) {
      minZoomType = minZoomType.toLowerCase();
    }

    const containerWidth = this.containerWidth;
    const containerHeight = this.containerHeight;
    const gridWidth = this.getGridWidth;
    const gridHeight = this.getGridHeight;
    const scaleX = containerWidth / gridWidth;
    const scaleY = containerHeight / gridHeight;
    // const scale = scaleX < scaleY ? scaleX : scaleY;

    let minZoom = MIN_ZOOM;
    if (minZoomType === 'cover' || minZoomType === 'fill') {
      minZoom = scaleX > scaleY ? scaleX : scaleY;
    } else if (minZoomType === 'contain' || minZoomType === 'fit') {
      minZoom = scaleX < scaleY ? scaleX : scaleY;
    }
    minZoom = minZoom > MIN_ZOOM ? minZoom : MIN_ZOOM;
    const maxZoom = MAX_ZOOM;

    newZoom = newZoom < minZoom ? minZoom : newZoom;
    newZoom = newZoom > maxZoom ? maxZoom : newZoom;

    return newZoom;
  }

  setZoom(value) {
    let { viewport } = this.state;

    let newZoom = value;

    if (typeof (value) === 'number') {
      newZoom = this.limitZoom(newZoom);
    }

    if (newZoom === this.getViewportZoom()) {
      return;
    }

    viewport = this.setViewportValues(viewport.x, viewport.y, newZoom, viewport);

    this.setState({
      viewport,
    }, this.notifyViewport());
  }

  zoomGrid(value) {
    const vpZoom = this.getViewportZoom();
    let { viewport } = this.state;
    let newZoom = this.calcZoom(vpZoom) + value;

    newZoom = this.limitZoom(newZoom);

    if (newZoom === vpZoom) {
      return;
    }

    viewport = this.setViewportValues(viewport.x, viewport.y, newZoom, viewport);

    this.setState({
      viewport,
    }, this.notifyViewport());
  }

  getFloorHeight() {
    return 180;
  }

  getFloorY() {
    return this.getGridHeight() - 150;
  }

  getItemComponentInfo(obj, create) {
    let map = this.componentInfoMap;
    if (!map && !create) {
      return null;
    }

    if (!map) {
      if (typeof (WeakMap) !== 'undefined') {
        map = new WeakMap();
      } else if (typeof (Map) !== 'undefined') {
        map = new Map();
      }
      this.componentInfoMap = map;
    }
    let res = map.get(obj);

    if (res || !create) {
      return res;
    }
    res = {};
    map.set(obj, res);

    return res;
  }

  getConfigScale() {
    return 0.5;
  }

  focusInfoBubble() {
    const { infoBubbleRef } = this;
    if (infoBubbleRef && infoBubbleRef.focusInput) {
      infoBubbleRef.focusInput();
    }
  }

  editInfoBubble() {
    const { infoBubbleRef } = this;
    if (infoBubbleRef) {
      if (infoBubbleRef.setEditing) {
        infoBubbleRef.setEditing(true);
      }
      if (infoBubbleRef.focusInput) {
        infoBubbleRef.focusInput(true);
      }
    }
  }

  getOpenedNote() {
    const { noteLayerRef } = this;
    if (!noteLayerRef) {
      return null;
    }
    return noteLayerRef.getOpenedNote();
  }

  closeOpenedNote() {
    const { noteLayerRef } = this;
    if (!noteLayerRef) {
      return null;
    }
    return this.noteLayerRef.closeOpenedNote();
  }

  setNoteOpened(note, opened, updateState = true) {
    const { noteLayerRef } = this;
    if (!noteLayerRef) {
      return;
    }
    noteLayerRef.setNoteOpened(note, opened, updateState);
  }

  toggleNoteOpened(note) {
    const { noteLayerRef } = this;
    if (!noteLayerRef) {
      return;
    }
    this.noteLayerRef.toggleNoteOpened(note);
  }

  /* Render */

  renderNotes(x, y, zoom) {
    const {
      showNotes,
      noteScale,
      notesEditable,
      notesRemovable,
      onClickNote,
      onClickNoteOption,
      config,
    } = this.props;

    if (showNotes === false) {
      return null;
    }
    const onUpdateInfoBubbleContent = this.updateNoteContentHandler;
    const onUpdateNotes = this.updateNoteContentHandler;

    const onPressNote = this.pressNoteHandler;

    const notesLayer = (
      <NoteLayer
        ref={this.noteLayerRefHandler}
        config={config}

        x={x}
        y={y}
        zoom={zoom}

        noteScale={noteScale}

        notesEditable={notesEditable}
        notesRemovable={notesRemovable}

        onGetTranslation={this.getTranslation}
        onPressNote={onPressNote}
        onClickNote={onClickNote}
        onClickNoteOption={onClickNoteOption}
        onUpdateInfoBubbleContent={onUpdateInfoBubbleContent}
        onUpdateNotes={onUpdateNotes}
        onDraggedNote={this.props.onDraggedNote}
        onRemoveNote={this.props.onRemoveNote}
        onRemovedNote={this.props.onRemovedNote}
      />
    );

    if (this.props.onRenderNotesLayer) {
      const res = this.props.onRenderNotesLayer(notesLayer, getConfigNotes(this.props.config), x, y, zoom, this);
      if (res !== false) {
        // if onRenderNotesLayer returned false, the default rendering of the notes layer is used
        return res;
      }
    }

    return (
      <div className="config-note__container">
        {notesLayer}
      </div>
    );
  }

  tryUpdateLayout(props = null) {
    const propsObject = props || this.props;
    let layout = this.currentLayout;
    const oldLayout = layout;
    let updated = false;
    let errors = null;
    if (!layout || propsObject.configVersion !== this.configVersion) {
      const { config, configProducts, configVersion, onUpdateLayout } = propsObject;
      layout = propsObject.layout;

      this.configVersion = configVersion;
      if (!layout) {
        errors = [];
        const options = {
          errors,
          scale: this.getConfigScale(),
          products: configProducts,
        };
        layout = createConfigLayout(config, options);
        updated = true;
        if (layout) {
          const bounds = layout.bounds;
          if (bounds) {
            bounds.minx -= 10;
            bounds.maxx += 10;
            bounds.miny -= 10;
            bounds.maxy += 10;

            bounds.width = bounds.maxx - bounds.minx;
            bounds.height = bounds.maxy - bounds.miny;
          }
        }
      }
      // Move image info
      // This is necessary to fix some layout issues
      // E.g. the chimney has to be positioned at kettle.y - kettleImage.top
      if (oldLayout) {
        const oldLayoutElements = oldLayout.elements;
        const numOldLayoutElements = oldLayoutElements ? oldLayoutElements.length : 0;
        const newLayoutElements = layout ? layout.elements : null;
        const numNewLayoutElements = newLayoutElements ? newLayoutElements.length : 0;
        let imageMapping = null;
        if (numNewLayoutElements > 0 && numOldLayoutElements > 0) {
          for (let i = 0; i < numOldLayoutElements; i += 1) {
            const element = oldLayoutElements[i];
            const { imageInfo } = element;
            if (imageInfo) {
              const id = element.data ? element.data.id || '' : '';
              const name = element.name || '';
              const key = `${name}_${id}`;
              if (!imageMapping) {
                imageMapping = {};
              }
              // store image data of previous item by slot & id
              imageMapping[key] = imageInfo;
            }
          }
          if (imageMapping) {
            for (let i = 0; i < numNewLayoutElements; i += 1) {
              const element = newLayoutElements[i];
              if (!element.imageInfo) {
                const id = element.data ? element.data.id || '' : '';
                const name = element.name || '';
                const key = `${name}_${id}`;
                element.imageInfo = imageMapping[key];
              }
            }
          }
        }
      }
      if (layout !== this.currentLayout) {
        this.currentLayout = layout;
        this.layoutFixesAfterLoad();

        const viewportZoom = this.getViewportZoom();
        const viewport = this.getZoomToFitViewport();
        viewport.zoom = viewportZoom;
        this.state.viewport = viewport;

        if (updated) {
          // Only set state directly if calling this method from the render method
          this.updateViewportValues(this.rendering);
          if (onUpdateLayout) {
            onUpdateLayout(layout, config, errors, this);
          }
        }
      }
    }
  }

  renderConfig(x, y, zoom) {
    const layout = this.currentLayout;

    if (!layout) {
      return null;
    }

    const { elements, connections } = layout; // eslint-disable-line
    const renderItems = [];

    if (connections) {
      const num = connections.length;
      for (let i = 0; i < num; i += 1) {
        const elem = this.renderConfigConnection(connections[i], i, layout);
        if (elem) {
          renderItems.push(elem);
        }
      }
    }

    let loadingImageURLs = null;
    const hasLoadedCallback = this.hasLoadedCallback();
    const { originalSystem } = this.props;

    if (elements) {
      const num = elements.length;
      for (let i = 0; i < num; i += 1) {
        const item = {
          ...elements[i],
          originalSystem,
        };

        if (hasLoadedCallback) {
          loadingImageURLs = ConfigComponent.collectConfigObjectImageURLs(item, loadingImageURLs);
        }

        const elem = this.renderConfigElement(item, i, layout);
        if (elem) {
          renderItems.push(elem);
        }
      }
    }

    this.loadingURLs = loadingImageURLs;

    const tx = Math.round(x);
    const ty = Math.round(y);

    const transform = cssTransform(`translate(${tx}px, ${ty}px) scale(${zoom},${zoom})`);

    return (
      <div className="configurator__image-container" style={{ transform, transformOrigin: '0 0' }}>
        {renderItems}
      </div>
    );
  }

  renderConfigConnection(connection, index, layout) {
    if (!connection) {
      return null;
    }

    const bounds = layout ? layout.bounds : null;
    const layoutX = bounds ? -(bounds.minx || 0) : 0;
    const layoutY = 0;// bounds ? bounds.miny || 0 : null;

    const floor = this.getFloorY();

    const { color, inverted } = connection;

    let { x1, y1, x2, y2, baseY } = connection;

    if (typeof (baseY) === 'number') {
      baseY = floor - baseY;
    }

    y1 = (floor - y1);
    y2 = (floor - y2);

    x1 += layoutX;
    y1 += layoutY;
    x2 += layoutX;
    y2 += layoutY;

    if (typeof (baseY) === 'number') {
      baseY += layoutY;
    }

    const key = `connection-${index}`;

    return (
      <BoxLine
        key={key}
        x1={x1}
        y1={y1}
        x2={x2}
        y2={y2}
        base={baseY}
        color={color}
        invertBase={inverted}
      />
    );
    //
  }

  renderConfigElement(element, index, layout) {
    const bounds = layout ? layout.bounds : null;
    const layoutX = bounds ? -(bounds.minx || 0) : 0;
    const layoutY = 0; // bounds ? bounds.miny || 0 : null;

    const floor = this.getFloorY();
    const X = Math.round(element.x + layoutX);
    const Y = Math.round((floor - element.y) + layoutY);
    const key = element.name;

    // const imageLoadedHandler = this.hasLoadedCallback() ? this.componentImageLoadedHandler : null;
    const imageLoadedHandler = this.componentImageLoadedHandler;
    return (
      <ConfigComponent
        key={key}
        objectData={element}
        onClickComponent={this.props.onClickComponent}
        onImageLoaded={imageLoadedHandler}
        x={X}
        y={Y}
      />
    );
    //
  }

  renderGrid() {
    const state = this.state;
    const viewport = state.viewport;
    const vpZoom = this.getViewportZoom();
    const zoom = this.calcZoom(vpZoom);

    const viewportRender = this.renderGridViewport(viewport.x, viewport.y, zoom);

    return viewportRender;
  }

  renderFloor(x, y, zoom) {
    if (!this.props.showFloor) {
      return null;
    }
    const floorHeight = this.getFloorHeight();

    const containerHeight = this.containerHeight;

    const viewY = this.getViewportY();

    const floorY = Math.round(((containerHeight * 0.5) - (viewY * zoom)) + ((this.getGridHeight() - floorHeight) * zoom));

    const floorStyle = {
      top: `${floorY}px`,
    };
    return (
      <div className="configurator__floor" style={floorStyle} />
    );
  }

  renderOverlay(x, y, zoom) {
    // Delegate rendering overlay to parent component
    // Used for rendering selection and stuff like that
    if (this.props.onRenderOverlay) {
      return this.props.onRenderOverlay(x, y, zoom);
    }
    return null;
  }

  renderGridViewport(x, y, zoom, overlay = null) {
    let styleObject = null;
    let className = 'config-view__workspace';
    const bgX = Math.round(-x * zoom);
    const bgY = Math.round(-y * zoom);

    const { backgroundColor, showGrid } = this.props;
    if (showGrid) {
      const gridUnitSize = 7;
      const gridUnitSizeOuter = gridUnitSize;
      const gridUnitSizeInner = gridUnitSize - 1;
      const gridColor = 'rgba(204, 204, 204, 0.25)';

      className = `${className} config-view__workspace--grid config-view__workspace--border`;

      styleObject = {
        backgroundImage: `repeating-linear-gradient(0deg, transparent, transparent ${gridUnitSizeInner}px, ${gridColor} ${gridUnitSizeInner}px, ${gridColor} ${gridUnitSizeOuter}px), repeating-linear-gradient(-90deg, transparent, transparent ${gridUnitSizeInner}px, ${gridColor} ${gridUnitSizeInner}px, ${gridColor} ${gridUnitSizeOuter}px)`,
        backgroundSize: `${gridUnitSizeOuter}px ${gridUnitSizeOuter}px`,
        backgroundPosition: `${bgX}px ${bgY}px`,
      };
    }
    if (backgroundColor) {
      if (!styleObject) {
        styleObject = {};
      }
      styleObject.backgroundColor = backgroundColor;
    }

    return (
      <div className={className} style={styleObject}>
        {this.renderFloor(x, y, zoom)}
        {this.renderGridContentViewport(x, y, zoom, overlay)}
      </div>
    );
  }

  renderGridContentViewport(x, y, zoom, overlay = null) {
    const containerWidth = this.containerWidth || 0;
    const containerHeight = this.containerHeight || 0;

    const zoomGridLeft = Math.round((containerWidth * 0.5) - (x * zoom));
    const zoomGridTop = Math.round((containerHeight * 0.5) - (y * zoom));

    const changedPos = ((this.absoluteViewportX !== zoomGridLeft) || (this.absoluteViewportY !== zoomGridTop));

    this.absoluteViewportX = zoomGridLeft;
    this.absoluteViewportY = zoomGridTop;

    if (changedPos) {
      this.notifyViewport();
    }

    const className = 'config-view__workspace';

    const { clickDragBackgroundManager } = this;
    clickDragBackgroundManager.setListener('onClick', this.clickBackgroundHandler);


    const content = (
      <div>
        <div
          className="configurator__background"
          onMouseDown={clickDragBackgroundManager.startDrag}
          onTouchStart={clickDragBackgroundManager.startDrag}
          role="button"
        />
        {this.renderConfig(zoomGridLeft, zoomGridTop, zoom)}
        {this.renderOverlay(zoomGridLeft, zoomGridTop, zoom)}
      </div>
    );

    let interactiveContent = content;
    const { pannable, zoomable } = this.props;
    if (pannable || zoomable) {
      interactiveContent = (
        <DragLayer
          onDrag={this.panGridHandler}
        >
          {content}
        </DragLayer>
      );
    }

    return (
      <div className={className} ref={this.workspaceRefHandler}>
        {interactiveContent}
        {this.renderNotes(zoomGridLeft, zoomGridTop, zoom)}
        {overlay}
      </div>
    );
  }

  // Delegate rendering of UI layer to parent component
  renderUI() {
    if (this.props.onRenderUI) {
      return this.props.onRenderUI(this);
    }
    return null;
  }

  render() {
    // UUGGGLLYYYYY
    this.rendering = true;
    // TODO: move tryUpdateLayout call to componentWillReceiveProps


    const className = classnames('config-view', this.props.className);
    const result = (
      <div style={this.props.style} className={className}>
        <div className="config-view__container" ref={this.getContainerRefHandler}>
          {this.renderGrid()}
        </div>
        {this.renderUI()}
        {/* this.renderDebugViewport() */}
      </div>
    );
    this.rendering = false;
    return result;
  }
  // #if DEBUG
  renderDebugViewport() {
    // Just for debugging purposes
    // I'm not going to create separate scss files for debug stuff
    // so everything is going into inline styles. Deal with it.
    const maxWidth = 300;
    const maxHeight = 300;

    const viewStyle = {
      fontSize: '0.8em',
      pointerEvents: 'none',
      width: `${maxWidth}px`,
      height: `${maxHeight}px`,
      position: 'absolute',
      left: '200px',
      top: '0px',
      border: '1px dotted rgba(0, 0, 0, 0.25)',
    };

    let scaleX = 1;
    let scaleY = 1;
    let scale = 1;

    const gridWidth = this.getGridWidth();
    const gridHeight = this.getGridHeight();

    const { containerWidth, containerHeight } = this;
    scaleX = maxWidth / gridWidth;
    scaleY = maxHeight / gridHeight;
    scale = scaleX < scaleY ? scaleX : scaleY;
    const scaledGridWidth = Math.round(gridWidth * scale);
    const scaledGridHeight = Math.round(gridHeight * scale);

    const { viewport } = this.state;
    const vpscale = this.getCalcZoom();
    const viewportWidth = Math.round((containerWidth / vpscale) * scale);
    const viewportHeight = Math.round((containerHeight / vpscale) * scale);
    let { x: viewportX, y: viewportY } = viewport;

    viewportX *= scale;
    viewportY *= scale;
    viewportX -= viewportWidth * 0.5;
    viewportY -= viewportHeight * 0.5;

    const { currentLayout } = this;
    const currentBounds = currentLayout ? currentLayout.bounds : null;
    const floorY = this.getFloorY();

    let boundsElem = null;

    if (currentBounds) {
      const { minx, maxx, miny, maxy } = currentBounds;
      const width = (maxx - minx) * scale;
      const height = (maxy - miny) * scale;
      const left = 0;
      const top = (floorY - maxy) * scale;
      const boundsStyle = {
        position: 'absolute',
        width: `${Math.round(width)}px`,
        height: `${Math.round(height)}px`,
        left: `${Math.round(left)}px`,
        top: `${Math.round(top)}px`,
        border: '1px solid blue',
      };
      boundsElem = (
        <div style={boundsStyle} />
      );
    }

    const gridStyle = {
      pointerEvents: 'none',
      width: `${scaledGridWidth}px`,
      height: `${scaledGridHeight}px`,
      position: 'absolute',
      left: '0px',
      top: '0px',
      border: '1px dashed rgba(0, 0, 0, 0.5)',
    };

    const viewportStyle = {
      pointerEvents: 'none',
      width: `${Math.round(viewportWidth)}px`,
      height: `${Math.round(viewportHeight)}px`,
      position: 'absolute',

      left: `${viewportX}px`,
      top: `${viewportY}px`,
      border: '1px solid black',
    };

    return (
      <div style={viewStyle}>
        <div style={gridStyle} />
        { boundsElem }
        <div style={viewportStyle}>
          <div>x: {Math.round(viewport.x)}</div>
          <div>y: {Math.round(viewport.y)}</div>
          <div>zoom: {viewport.zoom}</div>
          <div>real zoom: {vpscale}</div>
          <div>grid width: {gridWidth}</div>
          <div>grid height: {gridHeight}</div>
          <div>bounds: {JSON.stringify(currentBounds)}</div>
        </div>
      </div>
    );
  }
  // #endif
}

ConfigView.defaultProps = {
  limitPan: false,
};

ConfigView.propTypes = {
  className: PropTypes.string,
  style: PropTypes.object,

  intl: PropTypes.object,

  configVersion: PropTypes.any, // change version to trigger config changes
  config: PropTypes.any,
  configProducts: PropTypes.any, // eslint-disable-line

  layout: PropTypes.any, // eslint-disable-line

  viewZoom: PropTypes.number,
  viewX: PropTypes.number,
  viewY: PropTypes.number,

  showGrid: PropTypes.bool,
  showFloor: PropTypes.bool,
  backgroundColor: PropTypes.string,

  zoomable: PropTypes.bool,
  pannable: PropTypes.bool,

  limitPan: PropTypes.bool,

  onClickComponent: PropTypes.func,
  onClickBackground: PropTypes.func,

  showNotes: PropTypes.bool,
  noteScale: PropTypes.any,
  notesEditable: PropTypes.bool,
  notesRemovable: PropTypes.bool,

  onClickNote: PropTypes.func,
  onClickNoteOption: PropTypes.func,
  onPressNote: PropTypes.func,
  onUpdateNoteContent: PropTypes.func,
  onDraggedNote: PropTypes.func,
  onRemoveNote: PropTypes.func,
  onRemovedNote: PropTypes.func,

  onChangedViewPort: PropTypes.func,
  onWorkspaceRef: PropTypes.func,

  onRenderOverlay: PropTypes.func,
  onRenderUI: PropTypes.func,
  onRenderNotesLayer: PropTypes.func,

  onUpdateConfig: PropTypes.func,
  onLoaded: PropTypes.func,

  onUpdateLayout: PropTypes.func, // eslint-disable-line

  originalSystem: PropTypes.any,
};



// WEBPACK FOOTER //
// ./js/components/configuration/config-view.jsx